﻿<template>
    <div>
        <div
            v-if="selectedScope"
            class="columns">
            <project-list
                v-model:selected-project="selectedProject"
                :projects="projects"
                :selected-scope="selectedScope"
                :loading="projectsLoading" />
            <div
                class="column is-half">
                <div
                    class="is-align-content-end is-gap-10 mb-3">
                    <div class="is-pulled-right">
                        <b-button
                            v-require-can-edit-code="accessArguments"
                            type="is-primary"
                            @click="onNewProjectClicked">
                            <span>Create new project for {{ selectedScope }}</span>
                            <b-icon
                                icon="plus-circle-outline"
                                size="is-small" />
                        </b-button>
                    </div>
                </div>
                <section
                    v-if="selectedProject && !editFormActive">
                    <project-associations-panel
                        v-model="selectedAssociation"
                        :selected-project="selectedProject.Name" />
                    <spinner
                        :loading="loadingSingleProject">
                        <project-attributes-panel
                            :selected-project-attributes="selectedProjectAttributes"
                            :selected-scope="selectedAssociation"
                            :access-arguments="editAccessArguments"
                            @click-edit-project="onEditProjectClicked"
                            @click-copy-project="onCopyProjectClicked" />
                    </spinner>
                </section>
                <spinner
                    v-if="loading"
                    :loading="loading" />
                <div
                    v-if="editFormActive"
                    class="column is-one third">
                    <code-edit
                        :key="selectedProjectId"
                        use-box-render
                        :code-id="selectedProjectId"
                        :form-title="codeEditTitle"
                        :code-set-name="codesets[selectedAssociation]"
                        :fields-to-disable="['name']"
                        library="LCIProject"
                        :scopes="[selectedScope]"
                        form-title-class="is-4"
                        submit-button-label="Save"
                        :code-template-values="newProjectTemplate"
                        @refresh="afterProjectSave"
                        @cancel="cancelEditing" />
                </div>
            </div>
        </div>
        <b-message v-else>
            Please select a scope.
        </b-message>
    </div>
    <project-create-modal
        v-if="isNewProjectModalVisible"
        :scope="selectedScope"
        @on-cancel="isNewProjectModalVisible=false"
        @select="onExistingProjectSearched"
        @create="onProjectSelectedForTemplate" />
    <copy-code-modal
        v-if="showCopyModal"
        library="LCIProject"
        :codeset="codesets[selectedAssociation]"
        :code-identity="selectedProject.Name"
        @on-cancel="showCopyModal = false"
        @on-done="onFinishCopying" />
</template>

<script>
    import ProjectAttributesPanel from '@/apps/project/components/ProjectAttributesPanel.vue';
    import ProjectList from '@/apps/project/components/ProjectList.vue';
    import CodeEdit from '@/shared/codeedit/CodeEdit.vue';
    import { requireCanEditCode } from '@/shared/directives/requirePermission';
    import { getCodeSets, sqlQuery } from '@/shared/helpers/api';
    import { encodeIdBase64 } from '@/shared/helpers/utils';
    import Spinner from '@/shared/components/Spinner.vue';
    import { showMixin } from '@/shared/mixins/showMixin.js';
    import ProjectCreateModal from '@/apps/project/components/ProjectCreateModal.vue';
    import ProjectAssociationsPanel from '@/apps/project/components/ProjectAssociationsPanel.vue';
    import CopyCodeModal from '@/shared/components/CopyCodeModal.vue';

    export default {
        directives: {
            'require-can-edit-code': requireCanEditCode
        },
        components: { CopyCodeModal, ProjectAssociationsPanel, ProjectCreateModal, Spinner, ProjectAttributesPanel, ProjectList, CodeEdit },
        mixins: [
            showMixin
        ],
        props: {
            selectedScope: {
                type: String,
                required: false,
                default: () => null
            },
            navigateToProject: {
                type: String,
                required: false,
                default: () => null
            }
        },
        emits: [
            'updateProject'
        ],
        data() {
            return {
                projects: [],
                projectsLoading: false,
                selectedProject: null,
                selectedProjectData: null,
                selectedAssociation: null,
                editFormActive: false,
                codesets: null,
                isNewProjectModalVisible: false,
                showCopyModal: false,
                codesetsLoading: true,
                newProjectTemplate: null,
                loadingSingleProject: false
            };
        },
        computed: {
            accessArguments() {
                return {
                    libraryName: 'LCIProject',
                    scopes: [this.selectedScope]
                };
            },
            editAccessArguments() {
                return {
                    libraryName: 'LCIProject',
                    scopes: [this.selectedAssociation]
                };
            },
            selectedProjectId() {
                return this.selectedProjectData ? encodeIdBase64('Code', this.selectedProjectData.Id) : null;
            },
            codeEditTitle() {
                const verb = this.isEditing ? 'Edit' : 'New';
                return verb + ' Project for ' + this.selectedAssociation;
            },
            isEditing() {
                return this.editFormActive && !this.newProjectTemplate;
            },
            loading() {
                return this.projectsLoading || this.codesetsLoading;
            },
            selectedProjectAttributes() {
                const attributes = Object.keys(this.selectedProjectData)
                    .filter(key =>
                        !['Id', 'DeliveryCode', 'DeliveryCodeDescription'].includes(key)
                        && this.selectedProjectData[key] !== null
                        && this.selectedProjectData[key] !== '')
                    .map(key => ({
                        property: key,
                        value: this.selectedProjectData[key]
                    }));
                if (this.selectedProjectData['DeliveryCode']) {
                    attributes.push({
                        property: 'DeliveryCode',
                        value: this.selectedProjectData.DeliveryCode + ' - ' + this.selectedProjectData.DeliveryCodeDescription
                    });
                }

                return attributes;
            }
        },
        watch: {
            selectedScope: {
                async handler(newValue) {
                    if (newValue) {
                        await this.loadProjects();
                    } else {
                        this.projects = [];
                    }
                },
                immediate: true
            },
            selectedProject: {
                handler(newValue, oldValue) {
                    if (!oldValue) {
                        this.editFormActive = false;
                    }
                    if (this.selectedProject) {
                        this.selectedAssociation = this.selectedScope;
                        this.loadSingleProject();
                        this.$emit('updateProject', this.selectedProject.Name);
                    } else {
                        this.selectedProjectData = null;
                    }
                },
                immediate: true
            },
            selectedAssociation: {
                async handler(newValue) {
                    if (newValue) {
                        await this.loadSingleProject();
                    }
                }
            }
        },
        async mounted() {
            const res = await getCodeSets(this, 'LCIProject');
            const codesets = {};
            res.forEach(cs => {
                codesets[cs.scopes[0]] = cs.name; // LCIProject has exactly one scope per codeset
            });
            this.codesets = codesets;
            this.codesetsLoading = false;
        },
        methods: {
            async loadProjects() {
                this.resetUI();
                this.projectsLoading = true;
                const res = await sqlQuery(this, {
                    query: `
                        SELECT Id, Name, Description, IsValid, DeliveryCode, Remark
                        FROM LCIProject
                        WHERE Scope = @facility`,
                    arguments: { facility: this.selectedScope } });
                this.projects = res.data;
                this.openProject(this.navigateToProject);
                this.projectsLoading = false;
            },
            openProject(project) {
                if (project) {
                    this.selectedProject = this.projects.find(x => x.Name === project);
                }
            },
            resetUI() {
                this.selectedProject = null;
                this.isNewProjectModalVisible = false;
                this.editFormActive = false;
            },
            onNewProjectClicked() {
                this.selectedProject = null;
                this.selectedAssociation = this.selectedScope;
                this.editFormActive = false;
                this.isNewProjectModalVisible = true;
            },
            onEditProjectClicked() {
                this.editFormActive = true;
            },
            onCopyProjectClicked() {
                this.showCopyModal = true;
            },
            async afterProjectSave() {
                this.editFormActive = false;
                this.newProjectTemplate = null;
                await this.loadProjects();
            },
            onExistingProjectSearched(name) {
                this.isNewProjectModalVisible = false;
                this.selectedProject = this.projects.find(x => x.Name === name);
            },
            onProjectSelectedForTemplate(selection) {
                this.selectedProject = null;
                const project = selection.Template;
                this.newProjectTemplate = {
                    name: project.Name,
                    description: project.Description,
                    DeliveryCode: project.DeliveryCode || ''
                };
                this.isNewProjectModalVisible = false;
                this.editFormActive = true;
            },
            cancelEditing() {
                this.editFormActive = false;
                this.newProjectTemplate = null;
            },
            onFinishCopying() {
                this.showCopyModal = false;
                this.loadProjects();
            },
            async loadSingleProject() {
                this.loadingSingleProject = true;
                this.selectedProjectData = (await sqlQuery(this, {
                    query: `
                            SELECT TOP(1)
                                p.Id,
                                p.Name,
                                p.Description,
                                p.IsValid,
                                p.DeliveryCode,
                                dc.Description as DeliveryCodeDescription,
                                p.Remark
                            FROM LCIProject p LEFT JOIN LCIDeliveryCode dc ON p.DeliveryCode_ID = dc.Id
                            WHERE p.Name = @Name AND Scope = @Scope
                            `,
                    arguments: {
                        Name: this.selectedProject.Name,
                        Scope: this.selectedAssociation
                    }
                })).data[0];
                this.loadingSingleProject = false;
            }
        }
    };
</script>

<style scoped>
.headless-table thead {
    display: none;
}
</style>
